"use client";
import * as stylex from "@stylexjs/stylex";
import { Alert, Button } from "@softmax/design-system";
import { WikiSidebar } from "./navigation/wiki-sidebar";
import { WikiPageHeader } from "./page/wiki-page-header";
import { WikiResourceStatus } from "./wiki-resource-status";
import { WikiPageContent } from "./page/wiki-page-content";
import { useWikiMode } from "./use-wiki-mode";
import { localStyles } from "./wiki-view.styles";
import type {
  WikiLoadState,
  WikiPage,
  WikiPageEntry,
  WikiRevision,
  WikiPermissions,
  WikiMode,
  WikiEditorState,
  WikiDeletionState,
  WikiPageAddress,
  WikiRefreshHandler,
  WikiLinkRenderer,
} from "./wiki-types";
export type WikiViewProps = Readonly<{
  layout?: "page" | "embedded";
  pageSlug: string;
  displayTitle: string;
  wikiPageHref: WikiPageAddress;
  page: WikiLoadState<WikiPage | null>;
  index: WikiLoadState<readonly WikiPageEntry[]>;
  history: WikiLoadState<readonly WikiRevision[]>;
  permissions: WikiPermissions;
  mode: WikiMode;
  onModeChange(mode: WikiMode): void;
  onRefresh: WikiRefreshHandler;
  editor: WikiEditorState;
  deletion: WikiDeletionState;
  mutationError: string | null;
  onDismissMutationError(): void;
  revertingId: string | null;
  onRevert(revisionId: string): void;
  renderLink?: WikiLinkRenderer;
}>;
export function WikiView({
  layout = "page",
  pageSlug,
  displayTitle,
  wikiPageHref,
  page,
  index,
  history,
  permissions,
  mode,
  onModeChange,
  onRefresh,
  editor,
  deletion,
  mutationError,
  onDismissMutationError,
  revertingId,
  onRevert,
  renderLink = ({ children, ...props }) => <a {...props}>{children}</a>,
}: WikiViewProps) {
  const { diff, setDiff, modeItems, changeMode } = useWikiMode({
    page,
    permissions,
    onModeChange,
  });
  const Container = layout === "page" ? "main" : "section";
  return (
    <Container
      id={layout === "page" ? "main" : undefined}
      {...stylex.props(
        layout === "page" ? localStyles.main : localStyles.embedded,
      )}
    >
      <div {...stylex.props(localStyles.grid)}>
        <WikiSidebar
          refreshable={layout === "page"}
          index={index}
          wikiPageHref={wikiPageHref}
          pageSlug={pageSlug}
          onRefresh={onRefresh}
          renderLink={renderLink}
        />

        <article {...stylex.props(localStyles.article)}>
          <WikiPageHeader
            layout={layout}
            pageSlug={pageSlug}
            displayTitle={displayTitle}
            mode={mode}
            modeItems={modeItems}
            pending={editor.pending}
            onModeChange={changeMode}
          />
          <WikiResourceStatus
            refreshable={layout === "page"}
            state={page}
            label="page"
            onRefresh={() => onRefresh("page")}
          />
          {mutationError && !deletion.open && (
            <Alert tone="danger" announcement="assertive">
              <div {...stylex.props(localStyles.notice)}>
                <span>{mutationError}</span>
                <Button
                  size="sm"
                  variant="quiet"
                  onClick={onDismissMutationError}
                >
                  Dismiss
                </Button>
              </div>
            </Alert>
          )}

          <WikiPageContent
            layout={layout}
            page={page}
            index={index}
            history={history}
            permissions={permissions}
            mode={mode}
            onModeChange={changeMode}
            editor={editor}
            deletion={deletion}
            mutationError={mutationError}
            wikiPageHref={wikiPageHref}
            onRefresh={onRefresh}
            renderLink={renderLink}
            revertingId={revertingId}
            onRevert={onRevert}
            diff={diff}
            onDiff={setDiff}
          />
        </article>
      </div>
    </Container>
  );
}
